import React, { createContext, useContext, useEffect, useState } from "react";
import { User } from "../types/user";
import { getUsers, createUser, updateUser as updateUserApi, deleteUser as deleteUserApi } from "../api/userApi";
import { handleWithAlert } from "../utils/handleWithAlert";

interface UserContextProps {
  users: User[];
  addUser: (user: User) => void;
  updateUser: (user: User) => void;
  deleteUser: (id: string) => void;
  listUsers: () => Promise<void>;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [users, setUsers] = useState<User[]>([]);

  const listUsers = async () => {
    const result = await handleWithAlert(() => getUsers(), {
      errorMessage: 'Failed to fetch users',
    });
    if (result) setUsers(result);
  };

  const addUser = async (user: User) => {
    const newUser = await handleWithAlert(() => createUser(user), {
      successMessage: 'User created successfully',
      errorMessage: 'Failed to create user',
      showSuccess: true,
    });
    if (newUser) listUsers();
  };

  const updateUser = async (User: User) => {
    const updated = await handleWithAlert(() => updateUserApi(User.id.toString(), User), {
      successMessage: 'User updated',
      errorMessage: 'Failed to update user',
      showSuccess: true,
    });
    if (updated) listUsers();
  };

  const deleteUser = async (id: string) => {
    const deleted = await handleWithAlert(() => deleteUserApi(id), {
      successMessage: 'User deleted',
      errorMessage: 'Failed to delete user',
      showSuccess: true,
    });
    if (deleted !== null) setUsers(prev => prev.filter(u => u.id.toString() !== id));
  };

  return (
    <UserContext.Provider value={{ users, addUser, updateUser, deleteUser, listUsers }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be inside UserProvider");
  return context;
};